import type { AxiosRequestConfig } from 'axios'
import { serviceGo } from '@/utils/request'

/** 变量值类型，与 Go runtime.VarType 一致 */
export type VarValueType = 'number' | 'text'

/** 与 Go db.Variable 逐字段对应 */
export interface Variable {
  id: number
  game_id: number
  name: string
  type: VarValueType
  initial_value: string
  category_id: number | null // null = 未分类
  display_name: string
  sort: number
  created_at: string
  updated_at: string
}

/**
 * 列表接口返回的变量视图，多一个后端算好的读者可见性：
 * visible = display_name 非空 && 有种类 && 种类 app_visible（PRD §7.3）。
 * **直接用这个字段，不要在前端重算**——重算迟早与后端漂移。
 */
export interface VariableView extends Variable {
  visible: boolean
}

/** 与 Go db.VariableCategory 逐字段对应（该表没有 updated_at） */
export interface VariableCategory {
  id: number
  game_id: number
  parent_id?: number | null // Missing/null = first level; children inherit visibility.
  name: string
  app_visible: boolean
  sort: number
  created_at: string
}

/** 列表接口返回的种类视图，多一个成员计数 */
export interface VariableCategoryView extends VariableCategory {
  count: number
}

export interface VariableListData {
  variables: VariableView[]
  categories: VariableCategoryView[]
  uncategorized_count: number
}

/** 可传 `{ _silentError: true }` 跳过全局兜底弹窗（照 `deleteRoleGo` 的既有写法）——
    调用方要自己判 `code` 且自己处理网络错误/超时，不能指望这里弹窗提示。 */
export function listVariables(gameId: number, config?: AxiosRequestConfig & { _silentError?: boolean }) {
  return serviceGo.get('/api_v2/variables', { params: { game_id: gameId }, ...config })
}

/**
 * 新建变量。重名 → code 6002「变量名已存在」。
 * initial_value 按 type 校验：number 必须是 int32 范围整数，text 不超过 255 字符。
 */
export function createVariable(data: {
  game_id: number
  name: string
  type: VarValueType
  initial_value: string
  category_id?: number | null
  display_name?: string
}) {
  return serviceGo.post('/api_v2/variables', data)
}

/**
 * 部分更新，按 key 是否存在决定改哪些字段（JSON.stringify 会丢掉值为 undefined
 * 的 key，正好与后端语义对上；发送前千万别把 undefined 替换成 null）。
 * - **不要传 `type`**：后端返 1001「变量类型不可修改」
 * - **不要传 `name`**：后端会静默忽略，传了也不会生效、还会误导调用方
 * - `category_id` 三态：不传=不改、null=移到未分类、数字=目标种类
 * - 一个可更新字段都没传 → 1001「没有可更新的字段」
 */
export function updateVariable(
  id: number,
  data: { initial_value?: string; display_name?: string; category_id?: number | null }
) {
  return serviceGo.put(`/api_v2/variables/${id}`, data)
}

/** 被任何内容或角色引用 → code 6003「变量已被使用，不可删除」 */
export function deleteVariable(id: number) {
  return serviceGo.delete(`/api_v2/variables/${id}`)
}

/**
 * 批量移动种类。**整批原子**：任一 id 不属于本书 → 1001 整批拒绝。
 * 返回 `{affected}`（后端先对 ids 去重，affected = 去重后的数量），
 * **没有 skipped[]**——不要照抄素材库那套逐条列失败项的结果弹窗。
 */
export function batchMoveVariables(data: { ids: number[]; category_id: number | null }) {
  return serviceGo.post('/api_v2/variables/batch-move', data)
}

// ---------- 种类 ----------

/** data 是裸的 VariableCategory[]（不带 count；要计数用 listVariables） */
export function listVariableCategories(gameId: number) {
  return serviceGo.get('/api_v2/variable-categories', { params: { game_id: gameId } })
}

/** 二级创建由后端原子地保存 parent_id，并将父级直接成员移入新分组。 */
export function createVariableCategory(data: { game_id: number; name: string; app_visible: boolean; parent_id?: number | null }) {
  return serviceGo.post<{ code: number; message?: string; data: VariableCategory }>('/api_v2/variable-categories', data)
}

/** 部分更新：name / app_visible 都是可选，按 key 存在更新 */
export function updateVariableCategory(id: number, data: { name?: string; app_visible?: boolean }) {
  return serviceGo.put(`/api_v2/variable-categories/${id}`, data)
}

/** 后端在事务里先把成员变量的 category_id 置 NULL 再删种类 */
export function deleteVariableCategory(id: number) {
  return serviceGo.delete(`/api_v2/variable-categories/${id}`)
}

/** 拖动排序：整组 id 按新顺序发送，后端按数组下标重写 sort。不要发单条 */
export function reorderVariableCategories(data: { game_id: number; ids: number[] }) {
  return serviceGo.post('/api_v2/variable-categories/reorder', data)
}
